import * as React from 'react';
import { cn } from '@/lib/utils/classNames';

/**
 * GGC V2. Empty State — Figma node 166:554 family (Component 03).
 *
 * Card shown when a list/grid has no data. Icon + title + description +
 * optional CTA. Designed to sit inside table containers or list panels.
 *
 * Spec: card r-lg, padding 24px, shadow-md, w-240. Icon: 44px circle on
 * bg-base. Title 12px semi-bold, description 11px regular text-text-gray.
 */
export interface GgcEmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Icon rendered inside the 44px circle. */
  icon?: React.ReactNode;
  /** Title text, e.g. "No campaigns found". */
  title: React.ReactNode;
  /** Supporting description. */
  description?: React.ReactNode;
  /** Optional CTA element (e.g. a `GgcButton`). */
  cta?: React.ReactNode;
}

const GgcEmptyState = React.forwardRef<HTMLDivElement, GgcEmptyStateProps>(
  ({ className, icon, title, description, cta, ...props }, ref) => {
    return (
      <div ref={ref} className="flex w-full min-h-[50vh] justify-center" {...props}>
        <div
          className={cn(
            'inline-flex min-w-[240px] max-w-[480px] flex-col items-center gap-[8px] p-[24px] text-center font-ggc',
            className
          )}
        >
          <span
            aria-hidden
            className="flex size-[44px] items-center justify-center rounded-full bg-ggc-bg-base text-ggc-text-gray"
          >
            {icon}
          </span>
          <p className="ggc-text-body-bold text-ggc-text-primary">{title}</p>
          {description && (
            <p className="w-full ggc-text-caption-m text-ggc-text-gray">
              {description}
            </p>
          )}
          {cta && <div className="mt-2">{cta}</div>}
        </div>
      </div>
    );
  }
);
GgcEmptyState.displayName = 'GgcEmptyState';

export { GgcEmptyState };
